export interface ApiErrorOptions {
	status: number;
	kind?: 'http' | 'network' | 'parse' | 'unknown' | 'aborted';
	key?: string;
	title?: string;
	detail?: string;
	errors?: string[];
	bodyText?: string;
	cause?: unknown;
}

export class ApiError extends Error {
	readonly status: number;
	readonly kind: NonNullable<ApiErrorOptions['kind']>;
	key?: string;
	readonly title?: string;
	readonly detail?: string;
	readonly errors?: string[];
	readonly bodyText?: string;
	constructor(message: string, init: ApiErrorOptions) {
		super(message, { cause: init.cause });
		this.name = 'ApiError';
		this.status = init.status;
		this.kind = init.kind ?? 'http';
		this.key = init.key;
		this.title = init.title;
		this.detail = init.detail;
		this.errors = init.errors;
		this.bodyText = init.bodyText;
	}
}

export function asApiError(error: unknown, key?: string): ApiError {
	const source =
		error instanceof ApiError
			? error
			: new ApiError(error instanceof Error ? error.message : String(error), {
					status: 0,
					kind: 'unknown',
					cause: error,
				});
	return new ApiError(source.message, { ...source, key: key ?? source.key, cause: source.cause });
}

export async function checkResponse(response: Response): Promise<Response> {
	if (response.ok) return response;
	const bodyText = await response.text().catch(() => '');
	let body: any;
	try {
		body = bodyText ? JSON.parse(bodyText) : undefined;
	} catch {}
	const title = typeof body?.title === 'string' ? body.title : undefined;
	const detail =
		typeof body?.detail === 'string'
			? body.detail
			: typeof body?.message === 'string'
				? body.message
				: undefined;
	const errors =
		body?.errors && typeof body.errors === 'object'
			? Object.values(body.errors)
					.flat()
					.filter((value): value is string => typeof value === 'string')
			: undefined;
	throw new ApiError(
		detail || title || errors?.join('; ') || response.statusText || 'Request failed',
		{
			status: response.status,
			title,
			detail,
			errors,
			bodyText,
		},
	);
}
